import { NavLink, Outlet, useParams } from "react-router-dom";
import { AlertCircle } from "lucide-react";
import { Button } from "~/components/ui/button";
import useNextGenHeader from "~/contexts/nextgen-header";
import { useFetcher } from "~/hooks/useFetcher";
import { cn } from "~/lib/utils";
import { ROUTE_NEXTGEN_VIEW } from "~/router/constants";
import { Agent, agentPath } from "~/services/agents";

const NAV_ITEMS = [
  { label: "Profile", path: "profile" },
  { label: "Skills", path: "skills" },
  { label: "Toolkits", path: "toolkits" },
  { label: "Connections", path: "connections" },
  { label: "Conversations", path: "conversations" },
] as const;

export const AgentDetail = () => {
  const { agentId } = useParams<{ agentId: string }>();
  const { data: agent, error, isLoading } = useFetcher<Agent>(agentPath(agentId!));

  useNextGenHeader({
    breadcrumbs: [
      { label: "Agents", href: `${ROUTE_NEXTGEN_VIEW}/agents` },
      ...(agent ? [{ label: agent.display_name }] : []),
    ],
    actions: [
      <Button key="share" variant="outline" size="sm" className="text-xs" disabled>
        Share
      </Button>,
    ],
  });

  if (isLoading) {
    return (
      <div className="flex min-h-full">
        <div className="w-48 shrink-0 border-r border-border p-3">
          {Array.from({ length: 5 }).map((_, i) => (
            <div key={i} className="mx-1 my-1 h-8 animate-pulse rounded-md bg-muted" />
          ))}
        </div>
        <div className="flex-1 p-6">
          <div className="h-64 animate-pulse rounded-lg bg-muted" />
        </div>
      </div>
    );
  }

  if (error || !agent) {
    return (
      <div
        role="alert"
        className="flex flex-col items-center gap-3 py-16 text-center text-destructive"
      >
        <AlertCircle className="size-8" aria-hidden="true" />
        <p className="text-sm font-medium">Failed to load agent</p>
      </div>
    );
  }

  return (
    <div className="flex min-h-full">
      <nav
        aria-label="Agent sections"
        className="flex w-48 shrink-0 flex-col gap-1 border-r border-border p-3"
      >
        {NAV_ITEMS.map(({ label, path }) => (
          <NavLink
            key={path}
            to={path}
            end
            className={({ isActive }) =>
              cn(
                "rounded-md px-3 py-2 text-sm font-medium transition-colors",
                isActive
                  ? "bg-accent text-accent-foreground"
                  : "text-muted-foreground hover:bg-accent/50 hover:text-foreground"
              )
            }
          >
            {label}
          </NavLink>
        ))}
      </nav>

      <div className="flex-1 p-6">
        <Outlet context={agent} />
      </div>
    </div>
  );
};
